/**
 * web/src/screens/Notebook/hooks/edit-detail.ts — the detail/edit/remove seam
 * of useNotebookState (card vncVrGk1hc9i, round two): the per-card text
 * editor and the day/place detail form with its place search. Pure move out
 * of the former hooks.ts — no behaviour change.
 */
import { useRef, useState } from 'react';
import { api } from '../../../shell/bridge';
import { type NotebookItem, type PlacePick } from '../types';
import type { NotebookCapture } from './capture';
import type { NotebookFeed } from './feed';

export function useNotebookEditDetail({ reload, enrichPasses }: {
  reload: NotebookFeed['reload'];
  enrichPasses: NotebookCapture['enrichPasses'];
}) {
  /* Card 67zOnRgx4YTI item 1 — the notebook was create-only, so a typo meant
   * a second card. One editor per card: which card is open, its title and its
   * text (links carry both, typed entries carry the body). */
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editText, setEditText] = useState('');
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  /* Founder email 2026-10-23: the edit box grows with what is written — at
     rows=3 it showed a scrollbar at three lines. Same scrollHeight mechanism
     the Composer owns; one edit is open at a time (editingId), so one ref
     serves both the typed-card and link edit forms. Past the 40vh cap the
     native scrollbar takes over (textarea.pear-chat-input's overflow), and
     below it the box simply grows. */
  const editTextRef = useRef<HTMLTextAreaElement>(null);

  /* Card 2qBAZOM2LFWa — a memory knows the day it happened and where. Both
     ride the card's own detail: a small pill on the face opens one disclosure
     holding a date and a place search, and the saved pair comes back to the
     face as one line. One card at a time, same rule as the text editor — two
     open detail forms on one screen is a form the user did not ask for.
     `placeResults` holds the search's candidates; they are names and
     addresses only, because the route strips the coordinates before they
     leave the worker. */
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detailDate, setDetailDate] = useState('');
  const [detailPlace, setDetailPlace] = useState('');
  const [detailBusy, setDetailBusy] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [placeQuery, setPlaceQuery] = useState('');
  const [placeBusy, setPlaceBusy] = useState(false);
  const [placeResults, setPlaceResults] = useState<PlacePick[]>([]);
  const [placeNote, setPlaceNote] = useState<string | null>(null);


  /** Card 67zOnRgx4YTI item 1 — open the editor on one card. Links open with
   *  title + description; a typed entry opens with its own text. */
  const startEdit = (item: NotebookItem) => {
    setEditError(null);
    // A link whose unfurl never landed has no title yet — the card face shows
    // its host, so the editor opens with the URL rather than a blank field.
    setEditTitle(item.entryType === 'link' ? ((item.title ?? '').trim() || item.url) : '');
    setEditText(item.entryType === 'link' ? (item.description ?? '') : (item.body ?? ''));
    setEditingId(item.itemId);
    /* Card 2wn9seit8zGi — "Edit" fired from the context panel hands back to
       the card's own inline form (one editor, one source of truth), so bring
       that card into view: on desktop the panel sits beside the feed and the
       form would otherwise open off the top of the list. */
    window.requestAnimationFrame(() => {
      const card = document.querySelector(`[data-context-card="${item.itemId}"]`);
      card?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditError(null);
  };

  /** Save the edit through the one PUT the server already enforces (owner
   *  only). A failed save keeps the draft on screen — same out-loud rule as
   *  the composers (card xNGOA5rZz9LV phase 3). */
  const saveEdit = async (item: NotebookItem) => {
    if (editBusy) return;
    setEditBusy(true);
    setEditError(null);
    try {
      const edit = item.entryType === 'link'
        ? { title: editTitle, description: editText }
        : { body: editText };
      await api(`/api/notebook/${item.itemId}`, { method: 'PUT', body: { edit } });
      setEditingId(null);
      // Card ggTQe2KbB0A3 MEDIUM-3 — a rewritten gift restarts the pending
      // poll: the search runs after the response, so the budget must be
      // fresh or the refreshed detail never gets watched for.
      if (item.entryType === 'gift') enrichPasses.current = 0;
      await reload();
    } catch (err) {
      console.error('[Pear] notebook edit failed', err);
      setEditError('That edit didn\'t save — your text is unchanged. Try again.');
    } finally {
      setEditBusy(false);
    }
  };

  /** Card 2qBAZOM2LFWa — open the detail form for one card, seeded from what
   *  the row already holds. `event_date` is stored as epoch ms; the date
   *  input speaks `yyyy-mm-dd`, so both halves convert through UTC — the day
   *  a memory happened must not shift because of the timezone the reader is
   *  in. */
  const openDetail = (item: NotebookItem) => {
    setDetailId(item.itemId);
    setDetailDate(item.eventDate ? new Date(item.eventDate).toISOString().slice(0, 10) : '');
    setDetailPlace(item.place ?? '');
    setDetailError(null);
    setPlaceQuery('');
    setPlaceResults([]);
    setPlaceNote(null);
  };

  const closeDetail = () => {
    setDetailId(null);
    setDetailError(null);
  };

  /** One search. The route meters it on the date planner's own daily ceiling
   *  and returns the refreshed usage, so the cost line is the truth and not
   *  an estimate held elsewhere. */
  const searchPlaces = async () => {
    const text = placeQuery.trim();
    if (!text || placeBusy) return;
    setPlaceBusy(true);
    setPlaceNote(null);
    try {
      const res = await api<{ places: PlacePick[]; capped: boolean; reason: string | null; usage: { estCostUsd: number } }>(
        '/api/notebook/places',
        { method: 'POST', body: { text } },
      );
      setPlaceResults(res.places ?? []);
      if (res.capped) {
        setPlaceNote("That's today's location search limit — the plain text box below still works.");
      } else if ((res.places ?? []).length === 0) {
        setPlaceNote("Nothing came back for that — type the place in yourself and it'll save fine.");
      }
    } catch (err) {
      console.error('[Pear] place search failed', err);
      setPlaceNote("Couldn't search just now — type the place in yourself.");
      setPlaceResults([]);
    } finally {
      setPlaceBusy(false);
    }
  };

  /** Save the day and the place. A cleared field is a real clear: sending
   *  `null` takes the value off the card rather than leaving it behind. */
  const saveDetail = async (item: NotebookItem) => {
    if (detailBusy) return;
    setDetailBusy(true);
    setDetailError(null);
    try {
      await api(`/api/notebook/${item.itemId}`, {
        method: 'PUT',
        body: {
          edit: {
            eventDate: detailDate ? Date.parse(`${detailDate}T12:00:00Z`) : null,
            place: detailPlace.trim() || null,
          },
        },
      });
      setDetailId(null);
      await reload();
    } catch (err) {
      console.error('[Pear] notebook detail save failed', err);
      setDetailError("That detail didn't save — what's on the card is unchanged. Try again.");
    } finally {
      setDetailBusy(false);
    }
  };

  /** PRD E2 — the panel's correction writes through the SAME edit verb the
   *  card's detail form uses (one save path for `place`), then the feed
   *  re-reads its rows so the chip updates where the person is looking. */
  const savePlace = async (item: NotebookItem, place: string) => {
    await api(`/api/notebook/${item.itemId}`, {
      method: 'PUT',
      body: { edit: { place: place.trim() || null } },
    });
    await reload();
  };

  return {
    editingId, setEditingId, editTitle, setEditTitle, editText, setEditText,
    editBusy, setEditBusy, editError, setEditError, editTextRef,
    detailId, setDetailId, detailDate, setDetailDate, detailPlace, setDetailPlace,
    detailBusy, setDetailBusy, detailError, setDetailError,
    placeQuery, setPlaceQuery, placeBusy, setPlaceBusy,
    placeResults, setPlaceResults, placeNote, setPlaceNote,
    startEdit, cancelEdit, saveEdit,
    openDetail, closeDetail, searchPlaces, saveDetail, savePlace,
  };
}

export type NotebookEditDetail = ReturnType<typeof useNotebookEditDetail>;
